export const common = {
  nav: {
    home: 'Home',
    features: 'Features',
    cases: 'AI Cases',
    workflow: 'Workflow',
    contact: 'Contact',
    requestDemo: 'Demo',
  },
  hero: {
    title: 'AI-Powered Medical Imaging Analysis',
    subtitle: 'Comprehensive intelligent medical imaging system for automatic analysis of multiple conditions including fractures, osteoarthritis, lung abnormalities, scoliosis, and dental conditions',
    cta: 'Get Started',
    fdaCompliant: 'FDA Compliant',
    hipaaSecure: 'HIPAA Secure',
    confidence: 'Confidence',
    detected: 'Detected',
  },
  features: {
    label: 'CORE CAPABILITIES',
    title: 'Advanced Multi-Case Detection Technology',
    subtitle: 'Leveraging cutting-edge AI and computer vision to deliver fast, accurate, and comprehensive medical imaging analysis across multiple medical conditions for healthcare professionals worldwide.',
    
    fracture: {
      title: 'Bone Fracture Detection',
      description: 'Accurately detects multiple types of bone fractures including elbow, wrist, shoulder, humerus, forearm, and finger fractures with precise localization.',
      metric: '6 Fracture Types',
    },
    knee: {
      title: 'Knee Osteoarthritis Detection',
      description: 'Advanced analysis of knee joint degeneration, identifying osteoarthritis severity levels and providing early detection of cartilage deterioration.',
      metric: '5 Severity Grades',
    },
    lung: {
      title: 'Lung Abnormalities Detection',
      description: 'Comprehensive chest X-ray analysis detecting atelectasis, cardiomegaly, pleural effusion, infiltrates, nodules, masses, pneumonia, and pneumothorax with high accuracy.',
      metric: '8 Conditions',
    },
    scoliosis: {
      title: 'Scoliosis Detection',
      description: 'Identifies spinal curvature abnormalities and severity levels from X-ray images.',
      metric: '4 Spinal Regions',
    },
    teeth: {
      title: 'Teeth Detection',
      description: 'Detects all dental conditions including Caries, Crown, Filling, Implant, Bone Loss, and Fracture Teeth using X-ray images with YOLOv8 model.',
      metric: '31 Dental Conditions',
    },
    cavity: {
      title: 'Cavity Detection',
      description: 'Focuses specifically on detecting cavities versus normal teeth using real clinical images with YOLOv8 model. Supports JPEG/PNG with bounding box visualization.',
      metric: 'Real-time Detection',
    },
    annotated: {
      title: 'Annotated Images',
      description: 'Get detailed visual annotations highlighting detected conditions with bounding boxes and labels.',
      metric: 'Visual Clarity',
    },
    api: {
      title: 'High-Performance API',
      description: 'Built with FastAPI for fast, scalable performance and easy integration with web, mobile, and hospital systems.',
      metric: 'Fast Integration',
    },
  },
  cases: {
    badge: 'Clinical Applications',
    title: 'Comprehensive Medical Imaging Solutions',
    subtitle: 'Advanced AI-powered detection systems covering multiple medical specialties with clinical-grade accuracy and real-time analysis capabilities.',
    detectionTypes: 'Detection Types',
    keyFeatures: 'Key Features',
    aiDetection: 'AI Detection Active',
    tryDemo: 'Try Interactive Demo',
    
    // Detection type translations
    detectionTypeLabels: {
      // Fracture types
      elbow: 'Elbow',
      wrist: 'Wrist',
      shoulder: 'Shoulder',
      humerus: 'Humerus',
      forearm: 'Forearm',
      finger: 'Finger',
      
      // Knee grades
      normal: 'Normal',
      doubtful: 'Doubtful',
      minimal: 'Minimal',
      moderate: 'Moderate',
      severe: 'Severe',
      
      // Lung conditions
      atelectasis: 'Atelectasis',
      cardiomegaly: 'Cardiomegaly',
      effusion: 'Pleural Effusion',
      infiltrates: 'Infiltrates',
      nodules: 'Nodules',
      masses: 'Masses',
      pneumonia: 'Pneumonia',
      pneumothorax: 'Pneumothorax',
      
      // Dental conditions
      caries: 'Caries',
      crown: 'Crown',
      filling: 'Filling',
      implant: 'Implant',
      boneLoss: 'Bone Loss',
      fracture: 'Fracture',
      
      // Cavity detection
      cavity: 'Cavity',
      normalTooth: 'Normal Tooth',
      
      // Scoliosis regions
      cervical: 'Cervical',
      thoracic: 'Thoracic',
      lumbar: 'Lumbar',
      sacral: 'Sacral',
    },
    
    fracture: {
      title: 'Bone Fracture Detection',
      description: 'YOLO-based real-time fracture detection system covering multiple anatomical regions with high accuracy and clinical recommendations.',
      detectionLabel: 'Wrist Fracture Detected',
      feature1Title: 'Instant Detection',
      feature1Desc: 'Immediate fracture identification with location pinpointing and confidence scores.',
      feature2Title: 'Multi-Region Coverage',
      feature2Desc: 'Comprehensive analysis across 6 major anatomical regions including upper and lower extremities.',
      feature3Title: 'Clinical Integration',
      feature3Desc: 'Seamless PACS integration with automated reporting and treatment recommendations.',
    },
    
    knee: {
      title: 'Knee Osteoarthritis Detection',
      description: 'EfficientNet-B5 powered classification system that categorizes knee osteoarthritis into 5 severity grades for precise treatment planning.',
      detectionLabel: 'Grade 3 Moderate OA',
      feature1Title: '5-Grade Classification',
      feature1Desc: 'Precise severity assessment from Normal to Severe using validated clinical grading systems.',
      feature2Title: 'Progression Tracking',
      feature2Desc: 'Longitudinal analysis to monitor disease progression and treatment effectiveness over time.',
      feature3Title: 'Treatment Guidance',
      feature3Desc: 'Evidence-based recommendations for conservative or surgical interventions based on severity.',
    },
    
    teeth: {
      title: 'Dental Condition Detection',
      description: 'Comprehensive dental AI system detecting 31 different conditions from panoramic X-rays for complete oral health assessment.',
      detectionLabel: 'Multiple Conditions Detected',
      feature1Title: '31 Condition Types',
      feature1Desc: 'Complete coverage of dental pathologies, restorations, and orthodontic appliances in a single scan.',
      feature2Title: 'Panoramic Analysis',
      feature2Desc: 'Full-mouth assessment with precise localization of each detected condition and tooth numbering.',
      feature3Title: 'Treatment Planning',
      feature3Desc: 'Automated dental charting and comprehensive treatment plan generation for efficient workflows.',
    },
    
    scoliosis: {
      title: 'Scoliosis Detection',
      description: 'Advanced spinal curvature analysis system covering 4 spinal regions with Cobb angle measurement for accurate diagnosis.',
      detectionLabel: 'Thoracic Curvature 28°',
      feature1Title: 'Cobb Angle Measurement',
      feature1Desc: 'Automated calculation of spinal curvature angles with high precision and reproducibility.',
      feature2Title: '4 Spinal Regions',
      feature2Desc: 'Complete analysis of Cervical, Thoracic, Lumbar, and Sacral regions for comprehensive assessment.',
      feature3Title: 'Severity Classification',
      feature3Desc: 'Automatic grading from mild to severe with treatment recommendations and monitoring protocols.',
    },
  },
  workflow: {
    label: 'HOW IT WORKS',
    title: 'AI-Powered Analysis Pipeline',
    steps: {
      step1: {
        title: 'Upload Medical Image',
        description: 'User submits X-ray or medical image',
      },
      step2: {
        title: 'AI Detection',
        description: 'Model analyzes and detects conditions',
      },
      step3: {
        title: 'Generate Results',
        description: 'Confidence scores and annotations',
      },
      step4: {
        title: 'Detailed Analysis',
        description: 'Comprehensive condition assessment',
      },
      step5: {
        title: 'Treatment Guide',
        description: 'Recommendations generated',
      },
      step6: {
        title: 'Return Results',
        description: 'Annotated image and JSON response',
      },
    },
    stats: {
      analysisTime: 'Analysis Time',
      accuracyRate: 'Accuracy Rate',
      automatedService: 'Automated Service',
    },
  },
  cta: {
    title: 'Ready to Transform Your Diagnostic Workflow?',
    subtitle1: 'Join 500+ healthcare facilities',
    subtitle2: 'using AI-powered medical imaging analysis',
    button: 'Request Demo',
  },
  demo: {
    badge: 'Interactive Demo',
    title: 'See It In Action',
    subtitle: 'Experience the power of AI-driven medical imaging analysis across multiple conditions',
    selectCase: 'Select AI Case',
    uploadTitle: 'Upload Medical Image',
    uploadDesc: 'Drag and drop or click to upload',
    fileFormats: 'PNG, JPG, DICOM',
    minSize: 'Min 224x224px',
    sampleImage: 'Sample Image',
    analyzeButton: 'Analyze Image',
    analyzing: 'Analyzing...',
    results: 'Analysis Results',
    aiOutput: 'AI Analysis Output',
    confidence: 'Confidence',
    location: 'Location',
    condition: 'Condition',
    tryDemo: 'Try Demo',
    readyToAnalyze: 'Ready to Analyze',
    readyToAnalyzeDesc: 'Upload a medical image and click "Analyze Image" to see AI-powered detection results',
    cases: {
      fracture: 'Fracture Detection',
      knee: 'Knee Osteoarthritis',
      lung: 'Lung Abnormalities',
      scoliosis: 'Scoliosis Detection',
      teeth: 'Dental Conditions',
      cavity: 'Cavity Detection',
    },
  },
  contact: {
    title: 'Get in Touch',
    subtitle: 'Have questions? We\'re here to help you integrate our AI-powered solution',
    name: 'Full Name',
    email: 'Email Address',
    message: 'Your Message',
    send: 'Send Message',
    sendWhatsApp: 'Send via WhatsApp',
    info: {
      title: 'Contact Information',
      email: 'info@5vix.com',
      phone: '+20 1044553254',
    },
    businessHours: {
      title: 'Business Hours',
      weekdays: 'Monday - Friday: 9:00 AM - 6:00 PM',
      saturday: 'Saturday: 10:00 AM - 4:00 PM',
      sunday: 'Sunday: Closed',
    },
  },
  footer: {
    newsletter: {
      title: 'Stay Updated',
      subtitle: 'Get the latest updates on medical AI technology',
      placeholder: 'Type your message',
      button: 'Subscribe',
    },
    sendWhatsApp: 'Send via WhatsApp',
    quickLinks: 'Quick Links',
    contact: 'Contact Us',
    copyright: '© 2025 5vix. All rights reserved.',
    privacy: 'Privacy Policy',
    terms: 'Terms of Service',
    poweredBy: 'Powered by Debug',
  },
};
